// Web sign-in (prototype/admin/src/10-shell.js signInScreen()), with the real methods built so far:
// Google and the email code (spec 02 R-01). Two-step when the person has an authenticator.
import { Brand, Money } from '@xr/ui';
import { t } from '@xr/i18n';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { L, N, prefs, toggleLang } from '../lib/prefs';
import { planName, roleName } from '../lib/names';
import { app, google, openCompany, sendCode, signInPassword, signOut, useApp, verifyCode, verifyMfa } from '../lib/session';
import { DbError } from '../lib/supabase';
import { Btn, FakeQR, Field, Ic } from '../ui/kit';
import { toast } from '../ui/toast';

const tt = (key: string, ...a: unknown[]) => t(prefs.get().lang, key, ...a);
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
let lastEmail = ''; // kept when going back from the password or code step

function Otp({ onDone, err, busy }: { onDone: (code: string) => void; err?: string; busy?: boolean }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [v, setV] = useState(['', '', '', '', '', '']);
  useEffect(() => { refs.current[0]?.focus(); }, []);
  useEffect(() => { if (err) { setV(['', '', '', '', '', '']); refs.current[0]?.focus(); } }, [err]);
  const put = (i: number, d: string) => {
    const n = v.slice(); n[i] = d; setV(n);
    if (d && i < 5) refs.current[i + 1]?.focus();
    if (n.every(x => x) && !busy) onDone(n.join(''));
  };
  return (
    <>
      <div className="otp" dir="ltr">
        {v.map((x, i) => (
          <input key={i} id={'otp' + i} ref={el => { refs.current[i] = el; }} className="otp-i" inputMode="numeric" maxLength={1} autoComplete={i === 0 ? 'one-time-code' : 'off'}
            aria-label={L('Digit ' + (i + 1), 'الرقم ' + (i + 1))} value={x}
            onChange={e => put(i, e.target.value.replace(/\D/g, '').slice(-1))}
            onKeyDown={e => { if (e.key === 'Backspace' && !v[i] && i > 0) refs.current[i - 1]?.focus(); }}
            onPaste={e => {
              const d = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
              if (d.length === 6) { e.preventDefault(); setV(d.split('')); onDone(d); }
            }} />
        ))}
      </div>
      {err ? <p className="err"><Ic n="alert" />{err}</p> : null}
    </>
  );
}

const why = (e: unknown) => {
  const c = e instanceof DbError ? e.code : 'network';
  if (c === 'no_account') return L('No xReceived account uses this email. Create one in the phone app, or ask your company to invite you.', 'لا يوجد حساب xReceived بهذا البريد. أنشئ حساباً من تطبيق الهاتف أو اطلب دعوة من شركتك.');
  if (c === 'too_many') return L('Too many tries. Wait a minute and try again.', 'محاولات كثيرة. انتظر دقيقة وحاول مجدداً.');
  if (c === 'wrong_code') return tt('otpWrong');
  if (c === 'wrong_password') return L('That email and password don\'t match. Try again, or email yourself a code.', 'البريد وكلمة المرور غير متطابقين. حاول مجدداً أو أرسل رمزاً إلى بريدك.');
  return L('We couldn\'t reach xReceived. Check your connection and try again.', 'تعذر الوصول إلى xReceived. تحقق من الاتصال وحاول مجدداً.');
};

function StartStep({ err }: { err?: string }) {
  const [email, setEmail] = useState(() => lastEmail);
  useEffect(() => { if (err) toast(why(new DbError(err)), { tone: 'bad' }); }, [err]);
  const go = () => {
    const e = email.trim().toLowerCase();
    if (!EMAIL.test(e)) return toast(L('Enter a valid email address.', 'أدخل بريداً صحيحاً.'), { tone: 'bad' });
    lastEmail = e;
    app.set({ auth: { step: 'password', email: e } });
  };
  return (
    <>
      <h1>{L('Sign in', 'تسجيل الدخول')}</h1>
      <p className="muted">{L('Use the account you use on the xReceived phone app.', 'استخدم الحساب نفسه الذي تستخدمه في تطبيق xReceived.')}</p>
      <div className="auth-btns">
        <Btn icon="globe" cls="wide" onClick={() => void google().catch(x => toast(why(x), { tone: 'bad' }))}>{L('Continue with Google', 'المتابعة باستخدام Google')}</Btn>
      </div>
      <div className="or"><span>{L('or', 'أو')}</span></div>
      <Field label={L('Work email', 'البريد الإلكتروني للعمل')} htmlFor="au-email">
        <input id="au-email" className="inp" type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') go(); }} />
      </Field>
      <Btn kind="primary" cls="wide" onClick={go}>{L('Continue', 'متابعة')}</Btn>
    </>
  );
}

function PasswordStep({ email, err }: { email: string; err?: string }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const signIn = async () => {
    if (!pw.length) return app.set({ auth: { step: 'password', email, err: L('Enter your password.', 'أدخل كلمة المرور.') } });
    setBusy(true);
    try { await signInPassword(email, pw); }
    catch (x) { app.set({ auth: { step: 'password', email, err: why(x) } }); }
    finally { setBusy(false); }
  };
  const code = async () => {
    try { await sendCode(email); app.set({ auth: { step: 'code', email } }); }
    catch (x) { toast(why(x), { tone: 'bad' }); }
  };
  return (
    <>
      <button type="button" className="link back" onClick={() => app.set({ auth: { step: 'start' } })}><Ic n={prefs.get().lang === 'ar' ? 'chevR' : 'chevL'} />{email}</button>
      <h1>{L('Enter your password', 'أدخل كلمة المرور')}</h1>
      <Field label={L('Password', 'كلمة المرور')} htmlFor="au-pw" err={err}>
        <input id="au-pw" className="inp" type="password" autoComplete="current-password" placeholder="••••••••" value={pw} autoFocus
          onChange={e => setPw(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void signIn(); }} />
      </Field>
      <Btn kind="primary" cls="wide" busy={busy} onClick={() => void signIn()}>{L('Sign in', 'تسجيل الدخول')}</Btn>
      <div className="auth-alt">
        <button type="button" className="link" onClick={() => void code()}>{L('Email me a code instead', 'أرسل لي رمزاً بالبريد')}</button>
        <button type="button" className="link" onClick={() => void code()}>{L('Forgot password?', 'نسيت كلمة المرور؟')}</button>
      </div>
    </>
  );
}

function CodeStep({ email, err }: { email: string; err?: string }) {
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(30);
  useEffect(() => { if (wait <= 0) return; const id = setTimeout(() => setWait(w => w - 1), 1000); return () => clearTimeout(id); }, [wait]);
  const check = async (code: string) => {
    setBusy(true);
    try { await verifyCode(email, code); }
    catch (x) { app.set({ auth: { step: 'code', email, err: why(x) } }); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    try { await sendCode(email); setWait(30); toast(tt('codeResent'), { icon: 'mail' }); }
    catch (x) { toast(why(x), { tone: 'bad' }); }
  };
  const back = () => app.set({ auth: { step: 'start' } });
  return (
    <>
      <button type="button" className="link back" onClick={back}><Ic n={prefs.get().lang === 'ar' ? 'chevR' : 'chevL'} />{email}</button>
      <h1>{L('Check your email', 'تحقق من بريدك')}</h1>
      <p className="muted">{tt('codeSentTo', email)} {L('It works for 15 minutes.', 'صالح لمدة 15 دقيقة.')}</p>
      <Otp onDone={c => void check(c)} err={err} busy={busy} />
      <Btn kind="primary" cls="wide" busy={busy} onClick={() => {
        const code = Array.from(document.querySelectorAll<HTMLInputElement>('.otp-i')).map(i => i.value).join('');
        if (!/^\d{6}$/.test(code)) return app.set({ auth: { step: 'code', email, err: L('Enter all 6 digits.', 'أدخل الأرقام الستة.') } });
        void check(code);
      }}>{tt('verify')}</Btn>
      <div className="auth-alt">
        <button type="button" className="link" aria-disabled={wait > 0 ? 'true' : undefined} onClick={() => { if (wait <= 0) void resend(); }}>{wait > 0 ? tt('resendIn', wait) : tt('resendCode')}</button>
        <button type="button" className="link" onClick={back}>{L('Use a different email', 'استخدم بريداً آخر')}</button>
      </div>
    </>
  );
}

function MfaStep({ factorId, err }: { factorId: string; err?: string }) {
  const [busy, setBusy] = useState(false);
  const check = async (code: string) => {
    setBusy(true);
    try { await verifyMfa(factorId, code); }
    catch (x) { app.set({ auth: { step: 'mfa', factorId, err: why(x) } }); }
    finally { setBusy(false); }
  };
  return (
    <>
      <h1>{L('Two-step sign-in', 'التحقق بخطوتين')}</h1>
      <p className="muted">{L('Enter the 6-digit code from your authenticator app. Your company requires this for your role.', 'أدخل الرمز المكون من 6 أرقام من تطبيق المصادقة. شركتك تطلب ذلك لدورك.')}</p>
      <Otp onDone={c => void check(c)} err={err} busy={busy} />
      <Btn kind="primary" cls="wide" busy={busy} onClick={() => {
        const code = Array.from(document.querySelectorAll<HTMLInputElement>('.otp-i')).map(i => i.value).join('');
        if (!/^\d{6}$/.test(code)) return app.set({ auth: { step: 'mfa', factorId, err: L('Enter all 6 digits.', 'أدخل الأرقام الستة.') } });
        void check(code);
      }}>{L('Verify', 'تحقق')}</Btn>
      <button type="button" className="link center" onClick={() => void signOut()}>{L('Use a different email', 'استخدم بريداً آخر')}</button>
    </>
  );
}

function ChooseStep() {
  const { workspaces } = useApp();
  const pick = (id: string) => void openCompany(id).catch(x => toast(why(x), { tone: 'bad' }));
  return (
    <>
      <h1>{L('Choose a company', 'اختر شركة')}</h1>
      <p className="muted">{L('You belong to more than one. You can switch any time from the top of the menu.', 'أنت عضو في أكثر من شركة. يمكنك التبديل في أي وقت من أعلى القائمة.')}</p>
      <div className="ws-list">
        {workspaces.map(w => (
          <button key={w.tenant_id} type="button" className="ws-card" onClick={() => pick(w.tenant_id)}>
            <span className="ws-logo" style={{ ['--b' as string]: w.brand_color || '#0b5d4f' }}>{w.short}</span>
            <span><b>{N(w.name_en, w.name_ar)}</b><small>{planName(w.plan)} · {roleName(w.role)}</small></span>
            <Ic n={prefs.get().lang === 'ar' ? 'chevL' : 'chevR'} />
          </button>
        ))}
      </div>
    </>
  );
}

function AuthFrame({ children }: { children: ReactNode }) {
  const brand = (
    <div className="auth-brand">
      <div className="ab-top"><Brand kind="lockup" rev h={30} /></div>
      <div className="ab-stub">
        <div className="stub-row"><span>{L('Received from', 'استلمنا من')}</span><b>Sample Trading LLC</b></div>
        <div className="stub-row"><span>{L('Amount', 'المبلغ')}</span><b className="mono"><Money value={45000} /></b></div>
        <div className="stub-row"><span>{L('Receipt', 'سند')}</span><b className="mono">№ 4804</b></div>
        <div className="perf" />
        <div className="stub-foot">{L('Every dirham, from the customer\'s hand to your bank.', 'كل درهم، من يد العميل إلى بنكك.')}</div>
      </div>
      <p className="ab-note">{L('Admin panel for Team, Business and Enterprise companies. Collectors use the phone app.', 'لوحة الإدارة لشركات الفريق والأعمال والمؤسسات. المحصلون يستخدمون تطبيق الهاتف.')}</p>
    </div>
  );
  return (
    <div className="auth">{brand}<div className="auth-main"><div className="auth-card">{children}</div>
      <div className="auth-foot"><button type="button" className="link" onClick={toggleLang}>{prefs.get().lang === 'ar' ? 'English' : 'العربية'}</button><span>·</span><span>{L('Data stored in Frankfurt (EU)', 'البيانات مخزنة في فرانكفورت (الاتحاد الأوروبي)')}</span></div>
    </div></div>
  );
}

export function SignIn() {
  const { auth } = useApp();
  return (
    <AuthFrame>
      {auth.step === 'password' ? <PasswordStep key={auth.email} email={auth.email} err={auth.err} />
        : auth.step === 'code' ? <CodeStep key={auth.email} email={auth.email} err={auth.err} />
        : auth.step === 'mfa' ? <MfaStep factorId={auth.factorId} err={auth.err} />
        : auth.step === 'choose' ? <ChooseStep />
        : <StartStep err={auth.step === 'start' ? auth.err : undefined} />}
    </AuthFrame>
  );
}

/* Collectors work in the phone app (spec 02 R-28); people with no company get the same screen. */
export function PhoneOnly({ kind }: { kind: 'collector' | 'none' }) {
  const { me, co, session } = useApp();
  const first = (me?.name || session?.user.email || '').split(/[ @]/)[0];
  return (
    <div className="auth"><div className="auth-main solo"><div className="auth-card center">
      <div className="phone-art"><Ic n="phone" /></div>
      {kind === 'collector' ? (
        <>
          <h1>{L('Collectors use the phone app', 'المحصلون يستخدمون تطبيق الهاتف')}</h1>
          <p className="muted">{L(`Hi ${first}. As a collector at ${N(co?.name_en, co?.name_ar)}, you issue receipts and hand over money in the xReceived app. The web panel is for owners, admins, finance, supervisors and auditors.`, `مرحباً ${first}. كمحصل في ${N(co?.name_en, co?.name_ar)} تصدر السندات وتسلّم الأموال من تطبيق xReceived. لوحة الويب للمالكين والمسؤولين والمالية والمشرفين والمدققين.`)}</p>
        </>
      ) : (
        <>
          <h1>{L('No company to open here', 'لا توجد شركة لفتحها هنا')}</h1>
          <p className="muted">{L(`Hi ${first}. The web panel is for companies. Personal workspaces are managed in the xReceived app. If a company invited you, accept the invitation in the app.`, `مرحباً ${first}. لوحة الويب للشركات. مساحات العمل الشخصية تُدار من تطبيق xReceived. إن دعتك شركة فاقبل الدعوة من التطبيق.`)}</p>
        </>
      )}
      <div className="qr-box"><FakeQR /><div><b>{L('Scan to get the app', 'امسح لتحميل التطبيق')}</b><span>App Store · Google Play</span></div></div>
      {kind === 'collector' ? <p className="hint">{L('Need web access? Ask an admin to change your role.', 'تحتاج دخول الويب؟ اطلب من المسؤول تغيير دورك.')}</p> : null}
      <Btn kind="ghost" icon="logout" onClick={() => void signOut()}>{L('Sign out', 'تسجيل الخروج')}</Btn>
    </div></div></div>
  );
}
